import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import type { User } from 'firebase/auth';
import { useFeatureFlag } from '@/systems-shell/hooks/useFeatureFlag';
import { observeAuth, isFounder } from '@/lib/auth';
import { firebaseConfigured } from '@/firebase';
import { SignInCard } from '@/systems-shell/SignInCard';

// Gate flow for `/`:
//   1. If firebase not configured → treat as unauthed dev scaffold; render
//      whenOn so reviewers can browse the systems UI against mock data.
//   2. Else: wait for auth.
//   3. If no user → SignInCard (founder must land here to trigger the
//      on_for_founder branch of RTA_SYSTEMS_V1).
//   4. Else: check RTA_SYSTEMS_V1 via useFeatureFlag.
//      - flag on AND founder → whenOn
//      - flag on AND not founder → whenOff (dashboard is founder-only; no
//        other roles exist in v1)
//      - flag off → whenOff
//
// The "founder-only" constraint is stricter than rta-chat's flag gate:
// rta.systems renders NO UI for non-founder users when the flag is off,
// and even when the flag is on_for_all would never be flipped since the
// dashboard is an internal tool.
export function FlagGate({
  whenOn,
  whenOff,
}: {
  whenOn: ReactNode;
  whenOff: ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [authSettled, setAuthSettled] = useState(false);
  const { loading: flagLoading, isOn } = useFeatureFlag('RTA_SYSTEMS_V1');

  useEffect(() => {
    if (!firebaseConfigured) {
      setAuthSettled(true);
      return;
    }
    return observeAuth((u) => {
      setUser(u);
      setAuthSettled(true);
    });
  }, []);

  // Dev scaffold: surface the shell with mocks even without firebase so
  // reviewers can see the UI without a live project.
  if (!firebaseConfigured) return <>{whenOn}</>;

  if (!authSettled || flagLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="glass px-6 py-4 text-sm text-ink-600">Loading…</div>
      </div>
    );
  }

  if (!user) return <SignInCard />;

  // Founder-only extra guard: even if RTA_SYSTEMS_V1.on_for_all is flipped on,
  // the dashboard is intended as an internal tool. A non-founder seeing the
  // flag on still gets the placeholder.
  if (isOn && isFounder(user)) return <>{whenOn}</>;
  return <>{whenOff}</>;
}
